import * as React from 'react'
import { useContext, useState } from 'react'

import { Box, Button, Icon, Text, Touchable } from '@factorialco/deprecated-design-system'
import debounce from 'lodash/debounce'
import moment from 'moment'

import AutoCompleteSearchField from 'modules/core/components/AutoCompleteSearchField'
import DayPicker from 'modules/core/components/DayPicker'
import { GoogleMapsContext } from 'modules/core/components/GoogleMaps/GoogleMapsProvider'
import Select from 'modules/core/components/Select'
import { useCallback, useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMemo } from 'modules/core/hooks/useMemo'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useCostCentersByLegalEntity } from 'modules/cost_centers/hooks/useCostCentersByLegalEntity'
import { usePerDiemWizard } from 'modules/expenses/components/expenses/New/PerDiemWizard/form/context'
import {
  Destination,
  LocationData,
} from 'modules/expenses/components/expenses/New/PerDiemWizard/form/types'
import {
  calculateTripSummary,
  formatTripSummary,
} from 'modules/expenses/components/expenses/New/PerDiemWizard/form/utils'
import { useCalculatePerDiem } from 'modules/expenses/components/expenses/New/PerDiemWizard/hooks/useCalculatePerDiem'
import { useProjectsAssignedToEmployees } from 'modules/expenses/hooks/useProjects'
import { useBudgetOptions } from 'modules/finance/hooks/useBudgets'

import AdditionalInformation from './_AdditionalInformation'
import BudgetAndProject from './_BudgetAndProject'
import CostCenters from './_CostCenters'

const generateTimeOptions = () => {
  return [...Array(24)].flatMap((_, hour) =>
    [0, 30].map((minute) => ({
      label: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
      value: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
    }))
  )
}

const timeOptions = generateTimeOptions()

interface Suggestion {
  label: string
  value: string
}

// Hook for Google Maps location suggestions
// Always use English for consistent location data that the backend can process
const useLocationSuggestions = (placesLibraryLoaded: boolean) => {
  const [searchValue, setSearchValue] = useState<string>('')
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])

  const debouncedSearch = useMemo(
    () =>
      debounce((val: string) => {
        setSearchValue(val)
      }, 500),
    []
  )

  useEffect(() => {
    if (typeof google === 'undefined' || !placesLibraryLoaded) return

    if (searchValue.length === 0) {
      setSuggestions([])
      return
    }

    new google.maps.places.AutocompleteService().getPlacePredictions(
      { input: searchValue, language: 'en' },
      (placeSuggestions) => {
        setSuggestions(
          placeSuggestions?.map((suggestion) => ({
            label: suggestion.description,
            value: suggestion.place_id,
          })) || []
        )
      }
    )
  }, [searchValue, placesLibraryLoaded])

  const onSearch = useUnsafeCallback(
    (value: string) => {
      debouncedSearch(value)
    },
    [debouncedSearch]
  )

  const clearSuggestions = useCallback(() => {
    setSuggestions([])
  }, [])

  return { suggestions, onSearch, clearSuggestions }
}

// Hook to get place details from place ID
const usePlaceDetails = (placesLibraryLoaded: boolean) => {
  const inputRef = useLazyRef<HTMLInputElement | null>(() => null)

  const getPlaceDetails = useUnsafeCallback(
    (placeId: string): Promise<LocationData | null> => {
      return new Promise((resolve) => {
        if (!inputRef.current || typeof google === 'undefined' || !placesLibraryLoaded) {
          resolve(null)
          return
        }

        // Always request in English for consistent backend processing
        const request = {
          placeId,
          fields: ['geometry', 'address_components', 'name'],
          language: 'en',
        }
        const placeService = new google.maps.places.PlacesService(inputRef.current)

        placeService.getDetails(request, (result, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && result) {
            let country = ''
            let administrativeAreaLevel1 = ''
            let administrativeAreaLevel2 = ''
            let locality = ''

            for (const component of result.address_components ?? []) {
              const componentType = component.types[0]
              switch (componentType) {
                case 'country':
                  country = component.short_name.toUpperCase()
                  break
                case 'administrative_area_level_1':
                  administrativeAreaLevel1 = component.long_name
                  break
                case 'administrative_area_level_2':
                  administrativeAreaLevel2 = component.long_name
                  break
                case 'locality':
                  locality = component.long_name
                  break
              }
            }

            resolve({
              country,
              administrativeAreaLevel1: administrativeAreaLevel1 || undefined,
              administrativeAreaLevel2: administrativeAreaLevel2 || undefined,
              locality: locality || undefined,
            })
          } else {
            resolve(null)
          }
        })
      })
    },
    [inputRef, placesLibraryLoaded]
  )

  return { inputRef, getPlaceDetails }
}

interface DateTimeFieldProps {
  label: string
  date: Date | null
  helperText?: string
  onChange: (date: Date) => void
  minDate?: Date | null
}

const DateTimeField = ({ label, date, helperText, onChange, minDate }: DateTimeFieldProps) => {
  const dateComponent = date ? i18nDateToString(moment(date), 'ui') : ''
  const time = date ? moment(date).format('HH:mm') : undefined

  const handleDateChange = (dateString: string) => {
    if (!dateString) return

    const newDate = stringToDate(dateString, 'ui')
    if (date) {
      newDate.set({
        hours: moment(date).hours(),
        minutes: moment(date).minutes(),
      })
    } else {
      newDate.set({ hours: 9, minutes: 0 })
    }
    onChange(newDate.toDate())
  }

  const handleTimeChange = (timeString?: string) => {
    if (!timeString) return

    const [hours, minutes] = timeString.split(':').map(Number)
    const newDate = date ? moment(date) : moment()
    newDate.set({ hours, minutes })
    onChange(newDate.toDate())
  }

  return (
    <Box flexDirection="column" gap="s8">
      <Box flexDirection="row" alignItems="center" gap="s8">
        <Box width="s8" height="s8" background="grey400" borderRadius={{ all: 'abs100' }} />
        <Text.Normal size="200" weight="medium" color="grey1000">
          {label}
        </Text.Normal>
      </Box>
      <Box flexDirection="row" gap="s8" paddingLeft="s16">
        <Box flexGrow>
          <DayPicker
            mode="single"
            placeholder="--/--/----"
            minDate={minDate ? moment(minDate) : undefined}
            onChange={handleDateChange}
            value={dateComponent}
            focusTrapActive={false}
          />
        </Box>
        <Box width="s120">
          <Select
            none="--:--"
            readonly={!date}
            options={timeOptions}
            value={time}
            onChange={handleTimeChange}
          />
        </Box>
      </Box>
      {helperText && (
        <Box paddingLeft="s16">
          <Text.Normal size="100" color="grey600">
            {helperText}
          </Text.Normal>
        </Box>
      )}
    </Box>
  )
}

interface DestinationFieldProps {
  destination: Destination
  onUpdate: (updates: Partial<Destination>) => void
  onRemove: () => void
  canRemove: boolean
  inputRef: React.MutableRefObject<HTMLInputElement | null>
  suggestions: Suggestion[]
  onSearch: (value: string) => void
  onSelect: (placeId: string) => void
  clearSuggestions: () => void
  minDate?: Date | null
  showArrivalDate?: boolean
}

const DestinationField = ({
  destination,
  onUpdate,
  onRemove,
  canRemove,
  inputRef,
  suggestions,
  onSearch,
  onSelect,
  clearSuggestions,
  minDate,
  showArrivalDate = true,
}: DestinationFieldProps) => {
  const arrivalDate = destination.arrivalDatetime || null

  const handleChange = (value: string) => {
    onUpdate({ locationName: value })
    onSearch(value)
  }

  const handleSelect = (result: { value: string; label: string }) => {
    onUpdate({ locationName: result.label })
    onSelect(result.value)
    clearSuggestions()
  }

  const dateComponent = arrivalDate ? i18nDateToString(moment(arrivalDate), 'ui') : ''
  const time = arrivalDate ? moment(arrivalDate).format('HH:mm') : undefined

  const handleDateChange = (dateString: string) => {
    if (!dateString) return

    const newDate = stringToDate(dateString, 'ui')
    if (arrivalDate) {
      newDate.set({
        hours: moment(arrivalDate).hours(),
        minutes: moment(arrivalDate).minutes(),
      })
    } else {
      newDate.set({ hours: 9, minutes: 0 })
    }
    onUpdate({ arrivalDatetime: newDate.toDate() })
  }

  const handleTimeChange = (timeString?: string) => {
    if (!timeString) return

    const [hours, minutes] = timeString.split(':').map(Number)
    const newDate = arrivalDate ? moment(arrivalDate) : moment()
    newDate.set({ hours, minutes })
    onUpdate({ arrivalDatetime: newDate.toDate() })
  }

  return (
    <Box flexDirection="row" gap="s8" alignItems="center">
      <Box width="s8" height="s8" background="radicalRed600" borderRadius={{ all: 'abs100' }} />
      <Box flexDirection="column" gap="s4" flexGrow>
        <Text.Normal size="200" weight="medium" color="grey1000">
          {i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.destination')}
        </Text.Normal>
        <AutoCompleteSearchField
          value={destination.locationName}
          onChange={handleChange}
          onSelect={handleSelect}
          placeholder={i18n.t(
            'expenses_v2.forms.germany_per_diem_modal.trip_details.destination_placeholder'
          )}
          icon="pin"
          suggestions={suggestions}
          getRef={(ref) => {
            if (ref) inputRef.current = ref
          }}
        />
      </Box>
      {showArrivalDate && (
        <Box flexDirection="column" gap="s4" flexNoShrink>
          <Text.Normal size="200" weight="medium" color="grey1000">
            {i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.arrival')}
          </Text.Normal>
          <Box flexDirection="row" gap="s8" alignItems="center">
            <DayPicker
              mode="single"
              placeholder="--/--/----"
              minDate={minDate ? moment(minDate) : undefined}
              onChange={handleDateChange}
              value={dateComponent}
              focusTrapActive={false}
            />
            <Box width="s120">
              <Select
                none="--:--"
                readonly={!arrivalDate}
                options={timeOptions}
                value={time}
                onChange={handleTimeChange}
              />
            </Box>
          </Box>
        </Box>
      )}
      {canRemove && (
        <Touchable onClick={onRemove}>
          <Icon.Small icon="delete" color="grey600" />
        </Touchable>
      )}
    </Box>
  )
}

const VerticalConnector = () => (
  <Box paddingLeft="s4" height="s12">
    <Box width="s2" height="s12" background="grey200" />
  </Box>
)

const TripDetails = () => {
  const { data, updateData, setCalculation, addDestination, updateDestination, removeDestination } =
    usePerDiemWizard()
  const { placesLibraryLoaded } = useContext(GoogleMapsContext)
  const { employee } = useCurrentSession()

  // Use destinations from context
  const destinations = data.destinations

  const { suggestions, onSearch, clearSuggestions } = useLocationSuggestions(placesLibraryLoaded)
  const { inputRef, getPlaceDetails } = usePlaceDetails(placesLibraryLoaded)
  const { calculate } = useCalculatePerDiem()

  const employeeId = employee?.id
  const employeeLegalEntityId = employee?.legalEntity?.id

  // Format start date for budget options query
  const effectiveOnDate = data.startDate ? data.startDate.toISOString().split('T')[0] : undefined

  // Fetch budget options
  const { budgetOptions, loading: loadingBudgets } = useBudgetOptions({
    employeeId,
    effectiveOn: effectiveOnDate,
    skip: !employeeId,
  })

  // Fetch project workers
  const { projectWorkers, loading: loadingProjects } = useProjectsAssignedToEmployees({
    employeeIds: employeeId ? [employeeId] : [],
    skip: !employeeId,
  })

  // Fetch cost centers
  const { costCenters, loading: loadingCostCenters } = useCostCentersByLegalEntity({
    legalEntityId: employeeLegalEntityId,
    skip: !employeeLegalEntityId,
  })

  // Transform budget options to match component interface
  const budgetSelectOptions = budgetOptions.map((budget) => ({
    label: budget.label,
    value: budget.value.toString(),
  }))

  // Transform project workers to project options
  const projectSelectOptions = projectWorkers.map((pw) => ({
    label: pw.imputableProject?.name ?? '',
    value: pw.id.toString(),
  }))

  // Get subprojects for selected project
  const selectedProjectWorker = projectWorkers.find((pw) => pw.id === data.projectId)
  const subprojectSelectOptions =
    selectedProjectWorker?.imputableProject?.subprojects?.map((sp) => ({
      label: sp.name,
      value: sp.id?.toString() ?? '',
    })) ?? []

  // Transform cost centers to match component interface
  const costCenterSelectOptions = costCenters.map((cc) => ({
    id: cc.id,
    name: cc.name,
  }))

  const tripSummary = calculateTripSummary(data.startDate, data.endDate)

  // Sync first destination's arrival datetime with start date (you arrive at destination when trip starts)
  useUnsafeEffect(() => {
    if (data.startDate && destinations.length > 0) {
      const firstDest = destinations[0]
      // Only update if the arrival datetime differs from start date
      if (firstDest && firstDest.arrivalDatetime?.getTime() !== data.startDate.getTime()) {
        updateDestination(firstDest.id, { arrivalDatetime: data.startDate })
      }
    }
  }, [data.startDate, destinations, updateDestination])

  // Trigger calculation when dates, destinations, or exclusions change
  useUnsafeEffect(() => {
    const triggerCalculation = async () => {
      // Only calculate if we have valid data
      const hasValidDestinations = destinations.some(
        (dest) => dest.locationData.country && dest.arrivalDatetime
      )

      if (!employeeId || !data.startDate || !data.endDate || !hasValidDestinations) {
        setCalculation({ totalAmountCents: 0, isCalculating: false })
        return
      }

      setCalculation({ isCalculating: true })

      const result = await calculate({
        employeeId,
        startDate: data.startDate,
        endDate: data.endDate,
        destinations,
        dayExclusions: data.dayExclusions,
      })

      setCalculation(result)
    }

    triggerCalculation()
  }, [
    employeeId,
    data.startDate,
    data.endDate,
    destinations,
    data.dayExclusions,
    calculate,
    setCalculation,
  ])

  const handleAddStop = () => {
    addDestination()
  }

  const handleUpdateDestination = (id: string, updates: Partial<Destination>) => {
    updateDestination(id, updates)
  }

  const handleRemoveDestination = (id: string) => {
    removeDestination(id)
  }

  const handlePlaceSelect = async (destId: string, placeId: string) => {
    const locationData = await getPlaceDetails(placeId)
    if (locationData) {
      handleUpdateDestination(destId, { locationData })
    }
  }

  return (
    <Box flexDirection="column" gap="s24" padding="s24">
      <Box flexDirection="column" gap="s8">
        <Text.Normal size="300" weight="semibold" color="grey1000">
          {i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.routes_and_dates')}
        </Text.Normal>
        <Text.Normal size="200" color="grey700">
          {i18n.t(
            'expenses_v2.forms.germany_per_diem_modal.trip_details.routes_and_dates_description'
          )}
        </Text.Normal>
      </Box>

      <Box flexDirection="column" gap="s16">
        <DateTimeField
          label={i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.start_of_trip')}
          date={data.startDate}
          helperText={i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.start_helper')}
          onChange={(date) => updateData({ startDate: date })}
        />

        <VerticalConnector />

        {destinations.map((destination, index) => {
          // For first destination, min date is start date; for others, it's the previous destination's arrival
          const minDateForDestination =
            index === 0
              ? data.startDate
              : destinations[index - 1]?.arrivalDatetime || data.startDate
          // First destination uses the end date of the trip, so no need to show arrival date picker
          const isFirstDestination = index === 0

          return (
            <React.Fragment key={destination.id}>
              <DestinationField
                destination={destination}
                onUpdate={(updates) => handleUpdateDestination(destination.id, updates)}
                onRemove={() => handleRemoveDestination(destination.id)}
                canRemove={!isFirstDestination && destinations.length > 1}
                inputRef={inputRef}
                suggestions={suggestions}
                onSearch={onSearch}
                onSelect={(placeId) => handlePlaceSelect(destination.id, placeId)}
                clearSuggestions={clearSuggestions}
                minDate={minDateForDestination}
                showArrivalDate={!isFirstDestination}
              />
              {index < destinations.length - 1 && <VerticalConnector />}
            </React.Fragment>
          )
        })}

        <Box paddingLeft="s16">
          <Button type="secondary" onClick={handleAddStop}>
            {i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.add_stop')}
          </Button>
        </Box>

        <VerticalConnector />

        <DateTimeField
          label={i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.end_of_trip')}
          date={data.endDate}
          helperText={i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.end_helper')}
          onChange={(date) => updateData({ endDate: date })}
          minDate={data.startDate}
        />
      </Box>

      {tripSummary && (
        <Box
          flexDirection="row"
          alignItems="center"
          gap="s8"
          padding="s12"
          background="viridian100"
          borderRadius={{ all: 'abs008' }}
        >
          <Icon.Small icon="info-circle" color="viridian1000" />
          <Text.Normal size="200" weight="medium" color="viridian1000">
            {i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.trip_summary', {
              summary: formatTripSummary(tripSummary),
            })}
          </Text.Normal>
        </Box>
      )}

      <BudgetAndProject
        budgetOptions={budgetSelectOptions}
        projectOptions={projectSelectOptions}
        subprojectOptions={subprojectSelectOptions}
        loadingBudgets={loadingBudgets}
        loadingProjects={loadingProjects}
      />

      <CostCenters costCenterOptions={costCenterSelectOptions} loading={loadingCostCenters} />

      <AdditionalInformation />
    </Box>
  )
}

export default TripDetails
